import React, { useState, useEffect, useCallback } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import CommonInput from '../../Common/CommonInput';
import CommonDate from '../../Common/CommonDate';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import CommonIconRadio from '../../Common/CommonIconRadio';
import CommonTextarea from '../../Common/CommonTextarea';
import { fetchSettings } from '../../../store/modules/cashmemo/cashmemoActions';
import useFormSettings from '../../../hooks/useFormSettings';

import { 
    calculateGrossTotal, 
    calculateOverallDiscount,
    distributeHeaderDiscount,
    calculateTotals
} from './JobMemoCalculation';

import customerService from '../../../services/customerService';
import jobCostingService from '../../../services/jobcostingService';

import useModal from '../../../hooks/useModal';
import DataSelectionModal from '../../Common/CommonModal/DataSelectionModal';
import Customers from '../../../pages/Customers/Customers';
import { useHasPermission } from '../../../utils/permissionUtils';

const CashMemoForm = ({ initialData, onSave, isLoading, onClose }) => {

    const hasCreatePermission = useHasPermission(['create cash memos']);
    const hasUpdatePermission = useHasPermission(['update cash memos']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('cashmemo', fetchSettings);
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [typingTimer, setTypingTimer]         = useState(null);
    const doneTypingInterval = 800;

    const [formData, setFormData] = useState({
        id: '',
        job_order_id: '',
        invoice_number: '',
        invoice_date: '',
        customer: null,
        customer_name: '',
        address1: '',
        address2: '',
        phone_number: '',
        location: null,

        delivery_rule: {},
        gross_total: '',
        discount_percentage: '',
        discount_amount: '',
        sub_total: '',
        vat_amount: '',
        orders: [],
        items: [],
        job_costing: [],

        net_total: '',
        advance_payment: '',
        payment: '',
        balance: '',
        payment_type: '',
        payment_date:  new Date().toISOString().split('T')[0],
        payment_amount: '',
        payment_reference: '',
        payments: []
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingCustomerOrders, setIsLoadingCustomerOrders] = useState(false);
    const [isLoadingOrderItems, setIsLoadingOrderItems] = useState(false);


    // Calculate total payments and balance
    const calculatePaymentTotals = useCallback(() => {
        const totalPayments = formData.payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.payment_amount) || 0);
        }, 0);

        const netTotal = parseFloat(formData.net_total) || 0;
        const advancePayment = parseFloat(formData.advance_payment) || 0;
        const balance = netTotal - advancePayment - totalPayments;

        setFormData(prev => ({
            ...prev,
            payment: totalPayments.toFixed(3),
            balance: balance.toFixed(3)
        }));
    }, [formData.payments, formData.net_total, formData.advance_payment]);

    useEffect(() => {
        calculatePaymentTotals();
    }, [calculatePaymentTotals]);


    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                job_order_id: initialData.job_order_id || '',
                invoice_number: initialData.invoice_number || '',
                invoice_date: initialData.invoice_date || '',
                customer: initialData.customer || null,
                customer_name: initialData.customer_name || '',
                address1: initialData.address1 || '',
                address2: initialData.address2 || '',
                phone_number: initialData.phone_number || '',
                delivery_rule: initialData.delivery_rule || {},
                location: initialData.location || null,

                gross_total: initialData.gross_total || '',
                discount_percentage: initialData.discount_percentage || '',
                discount_amount: initialData.discount_amount || '',
                sub_total: initialData.sub_total || '',
                vat_amount: initialData.vat_amount || '',
                orders: initialData.orders || [],
                items: initialData.items || [],
                job_costing: initialData.job_costing || [],

                net_total: initialData.net_total || '',
                advance_payment: (parseFloat(initialData.advance_payment) || 0).toFixed(3),
                balance: ((parseFloat(initialData.net_total) || 0) - (parseFloat(initialData.advance_payment) || 0)).toFixed(3),
                payment: '',
                payment_type: initialData?.payment_type || '',
                payment_date: initialData?.payment_date || new Date().toISOString().split('T')[0],
                payment_amount: initialData?.payment_amount ? (parseFloat(initialData.payment_amount) || 0).toFixed(3) : '',
                payment_reference: initialData?.payment_reference || '',
                payments: initialData.payments ? initialData.payments.map(payment => ({
                    ...payment,
                    payment_amount: (parseFloat(payment.payment_amount) || 0).toFixed(3)
                })) : []
            });
        }
    }, [initialData]);

    const calculateFormData = (formData) => {
        const grossTotal = calculateGrossTotal(formData.items);

        const overallDiscountAmount = calculateOverallDiscount(
            grossTotal,
            parseFloat(formData.discount_percentage) || 0,
            parseFloat(formData.discount_amount) || 0
        );
        const itemsWithHeaderDiscount = distributeHeaderDiscount(
            formData.items,
            overallDiscountAmount
        );
        const totals = calculateTotals(itemsWithHeaderDiscount);

        return {
            gross_total: grossTotal.toFixed(3),
            discount_amount: overallDiscountAmount.toFixed(3),
            sub_total: (grossTotal - overallDiscountAmount).toFixed(3),
            vat_amount: totals.vatAmount.toFixed(3),
            net_total: totals.netTotal.toFixed(3),
            balance: (totals.netTotal - formData.advance_payment).toFixed(3),
            items: itemsWithHeaderDiscount
        };
    }

    const handleDiscountChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            return {
                ...prev,
                [name]: value
            }
        });

        if (value.length > 0) {
            clearTimeout(typingTimer);
            const timer = setTimeout(() => doneTyping(value, name), doneTypingInterval);
            setTypingTimer(timer);
        } else {
            doneTyping(value, name);
            clearTimeout(typingTimer);
        }
    }
    const doneTyping = async (value, name) => {
        setFormData(prev => {
            let newData = { ...prev };
            newData[name] = value;
            if(prev.gross_total*1 < value*1) {
                newData[name] = prev.gross_total;
            }

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            }; 
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            let newData = { ...prev };

            // Validate discount_percentage
            if (name === 'discount_percentage') {
                const numValue = parseFloat(value) || 0;
                if (numValue > 100) {
                    return prev; // Don't update if value exceeds 100
                }
            }

            newData[name] = type === 'checkbox' ? checked : value;
            const calculatedData = calculateFormData(newData);

            return {
                ...newData,
                ...calculatedData
            };
        });

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };
    const handleSelectChange = async (name, value) => {
        let orders = [];
        if (name === 'customer') {
            if (value) {
                try {
                    setIsLoadingCustomerOrders(true);
                    const response = await customerService.getCostedOrders(value.id);
                    orders = response?.data;
                } catch (error) {
                    console.error('Error fetching FG items:', error);
                } finally {
                    setIsLoadingCustomerOrders(false);
                }
            }

            setFormData(prev => ({
                ...prev,
                job_order_id: '',
                [name]: {
                    id: value?.id ?? '',
                    label: value?.label ?? '',
                } ?? null,
                customer_name: value?.customer_name ?? '',
                address1: value?.address1 ?? '',
                address2: value?.address2 ?? '',
                phone_number: value?.phone_number ?? '',
                orders: orders,
                
                job_costing: [],
                items: [],
                gross_total: '',
                discount_percentage: '',
                discount_amount: '',
                sub_total: '',
                vat_amount: '',
                net_total: '',
                advance_payment: '',
                balance: '',

                payment: '',
                payment_type: '',
                payment_amount: '',
                payment_reference: '',
                payments: []
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSelectOrder = async (order) => {
        let costedOrder = [];
        let items = [];

        try {
            setIsLoadingOrderItems(true);
            const response = await jobCostingService.getCostedOrder(order.id);
            costedOrder = response?.data;
            items = (costedOrder?.items || []).map(item => ({
                ...item,
                item_description: item?.item?.item_description ?? item?.item_description,
            }));

            setFormData(prev => {
                return {
                    ...prev,
                    job_order_id: order.job_order_id,
                    items: items,
                    job_costing: [order],

                    customer: order?.customer ?? null,
                    customer_name: order?.customer?.customer_name ?? '',
                    address1: order?.customer?.address1 ?? '',
                    address2: order?.customer?.address2 ?? '',
                    phone_number: order?.customer?.phone_number ?? '',
                    location: order?.location ?? null,
                    gross_total: costedOrder?.gross_total ?? '',
                    discount_percentage: costedOrder?.discount_percentage ?? '',
                    discount_amount: costedOrder?.discount_amount ?? '',
                    sub_total: costedOrder?.sub_total ?? '',
                    vat_amount: costedOrder?.vat_amount ?? '',
                    net_total: costedOrder?.net_total ?? '',
                    advance_payment: costedOrder?.advance_payment ?? '',
                    balance: costedOrder?.balance ?? '',


                    payment: '',
                    payment_type: '',
                    payment_amount: '',
                    payment_reference: '',
                    payments: []
                };
            });
        } catch (error) {
            console.error('Error fetching FG items:', error);
        } finally {
            setIsLoadingOrderItems(false);
        }
    }


    const handlePaymentChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Auto-fill payment_amount with balance when payment type is selected
        if (name === 'payment_type' && value) {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                payment_amount: parseFloat(prev.balance).toFixed(3) // Auto-fill with balance
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }

        // Validate payment amount against balance when it changes
        if (name === 'payment_amount') {
            const currentBalance = parseFloat(formData.balance) || 0;
            const paymentAmount = parseFloat(value) || 0;
            if (paymentAmount > currentBalance) {
                setErrors(prev => ({
                    ...prev,
                    payment_amount: `Payment amount cannot exceed remaining balance of ${currentBalance.toFixed(3)}`
                }));
            } else if (errors.payment_amount) {
                setErrors(prev => ({...prev, payment_amount: ''}));
            }
        } else if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };
    
    const handleAddPayment = () => {
        // Validate payment type
        if (!formData.payment_type) {
            setErrors(prev => ({
                ...prev,
                payment_type: 'Please select a payment type'
            }));
            return;
        }

        // Validate payment amount
        if (!formData.payment_amount || parseFloat(formData.payment_amount) <= 0) {
            setErrors(prev => ({
                ...prev,
                payment_amount: 'Payment amount must be greater than 0'
            }));
            return;
        }

        // Validate payment amount against balance
        const currentBalance = parseFloat(formData.balance) || 0;
        const paymentAmount = parseFloat(formData.payment_amount) || 0;
        if (paymentAmount > currentBalance) {
            setErrors(prev => ({
                ...prev,
                payment_amount: `Payment amount cannot exceed remaining balance of ${currentBalance.toFixed(3)}`
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            payment_type: '', 
            payment_amount: '', 
            payment_date: new Date().toISOString().split('T')[0], 
            payment_reference: '',
            payments: [...prev.payments, { 
                payment_type: prev.payment_type, 
                payment_amount: parseFloat(prev.payment_amount).toFixed(3), 
                payment_date: prev.payment_date, 
                payment_reference: prev.payment_reference 
            }]
        }));

        // Clear any existing errors after successful addition
        setErrors(prev => ({
            ...prev,
            payment_type: '',
            payment_amount: ''
        }));
    };

    const handleDeletePayment = (index) => {
        setFormData(prev => ({
            ...prev,
            payments: prev.payments.filter((_, i) => i !== index)
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);


    const customerModal = useModal();
    const customerExternalCondition = {
        rules: {
            condition: 'and',
            children: [
                {
                    type: 'rule',
                    query: {
                        operator: 'equal',
                        rule: 'users.customer_type',
                        type: 'text',
                        value: '2'
                    }
                }
            ]
        }
    };

    const handleCustomerSelected = (customer) => {
        const updatedValue = {
            id: customer?.id,
            label: customer?.label,
            customer_name: customer.name,
            address1: customer.address1,
            address2: customer.address2,
            phone_number: customer.phone
        }

        handleSelectChange('customer', updatedValue);
        customerModal.hide();
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || amount === '') return '0.00';
        return parseFloat(amount).toFixed(2);
    };

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Create Cash Memo</h5>
                </div>
                <div className="d-flex justify-content-end">
                    {formData?.id && (
                        <a href={`${process.env.PUBLIC_URL}/cash_memos/view/${formData.id}?preview=true`} target="_blank" className="btn btn-primary me-2" >
                            <i className="fas fa-print"></i> Print Preview
                        </a>
                    )}  
                </div>
            </div>
            <div className="flex-grow-1">

                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="invoice_number"
                            label="Invoice Number"
                            value={formData.invoice_number}
                            onChange={handleChange}
                            placeholder="Enter invoice number"
                            disabled={true}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="invoice_date"
                            label="Invoice Date"
                            value={formData.invoice_date}
                            onChange={handleChange}
                            placeholder="Enter invoice date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="customer"
                            label="Customer"
                            value={formData.customer}
                            onChange={(value) => handleSelectChange('customer', value)}
                            loadOptions={async (inputValue) => {
                                const condition = {
                                    rules: {
                                        condition: 'and',
                                        children: [
                                            {
                                                type: 'rule',
                                                query: {
                                                    operator: 'equal',
                                                    rule: 'users.customer_type',
                                                    type: 'text',
                                                    value: '2'
                                                }
                                            }
                                        ]
                                    }
                                }

                                const response = await customerService.getList({ search: inputValue }, condition);
                                return response.data.map(customer => ({
                                    id: customer.id,
                                    label: customer.name?.en +' ( '+customer.customer_code+' )',
                                    customer_name: customer.name,
                                    address1: customer.address1,
                                    address2: customer.address2,
                                    phone_number: customer.phone
                                }));
                            }}
                            isSubmitting={isSubmitting || formData?.id}
                            errors={errors}
                            required={true}
                            actionButtons={[
                                {
                                    icon: 'fa fa-bars',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => customerModal.show()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            name="customer_name"
                            label="Customer Name"
                            rows={1}
                            value={formData.customer_name}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="phone_number"
                            label="Phone Number"
                            value={formData.phone_number}
                            required={false}
                            disabled={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Address 1"
                            name="address1"
                            rows={1}
                            value={formData.address1}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="address2"
                            label="Address 2"
                            value={formData.address2}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="location"
                            label="Store Location"
                            value={formData.location?.label}
                            disabled={true}
                            required={false}
                        />
                    </div>
                </div>
                {
                    (!formData.id) && (
                        <div className="row">
                            <div className="col-md-12">
                                <h5 className="mb-3">Costed Job Orders</h5>
                                <div className="table-responsive mb-2">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Costing Number</th>
                                                <th>Job Order Number</th>
                                                <th>Job Order Date</th>
                                                <th>Customer</th>
                                                <th>Net Total</th>
                                                <th>Advance Amount</th>
                                                <th>Balance</th>
                                                <th>Trial Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.orders && formData.orders.length > 0 ? (
                                                formData.orders.map(order => (
                                                    <tr key={order.id}>
                                                        <td>
                                                            <input type="checkbox" 
                                                                className="form-check-input"
                                                                checked={formData.job_costing?.some(jc => jc.id === order.id)}
                                                                onChange={() => handleSelectOrder(order)}
                                                            />
                                                        </td>
                                                        <td>{order.costing_number}</td>
                                                        <td>{order.order_number}</td>
                                                        <td>{order.order_date}</td>
                                                        <td>{order.customer?.label}</td>
                                                        <td>{order.net_total}</td>
                                                        <td>{order.advance_payment}</td>
                                                        <td>{order.balance}</td>
                                                        <td>{order.trial_date}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center">No orders available</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )
                }
                <div className="row">
                    <div className="col-md-12">
                        <h5 className="mb-3">Order Items</h5>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="table-responsive mb-2">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Item Code</th>
                                        <th>Item Name</th>
                                        <th>Unit</th>
                                        <th>Qty</th>
                                        <th>Unit Price</th>
                                        <th>Discount %</th>
                                        <th>Line Amt.</th>
                                        <th>VAT Amt.</th>
                                        <th>Amount</th>
                                        <th>Amount Incl. VAT</th>
                                        <th>VAT Prod. Group</th>
                                        <th>VAT Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.items && formData.items.length > 0 ? (
                                        formData.items.map(item => (
                                            <tr key={item.id}>
                                                <td className="text-nowrap">{item?.item?.item_code}</td>
                                                <td className="text-nowrap">{item?.item_description || '-'}</td>
                                                <td className="text-nowrap">{item.unit}</td>
                                                <td className="text-nowrap">{item.quantity}</td>
                                                <td className="text-nowrap">{item.unit_price}</td>
                                                <td className="text-nowrap">{item.discount_percentage}</td>
                                                <td className="text-nowrap">{item.line_amount}</td>
                                                <td className="text-nowrap">{item.vat_amount}</td>
                                                <td className="text-nowrap">{item.amount_excl_vat}</td>
                                                <td className="text-nowrap">{item.amount_incl_vat}</td>
                                                <td className="text-nowrap">{item?.vat_product_group?.label}</td>
                                                <td className="text-nowrap">{item.vat_percentage}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="12" className="text-center">No items available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="row mt-4 d-flex justify-content-end">

                    <div className="col-12 col-md-6 order-md-2">
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label">Gross Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="gross_total"
                                    value={formData.gross_total}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label">Discount %:</label>
                            <div className="col-lg-6 col-md-6">
                                <CommonInput
                                    type="number"
                                    className="mb-0 custom-style text-end"
                                    name="discount_percentage"
                                    value={formData.discount_percentage}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                    min={0}
                                    max={100}
                                />
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label">Discount Amt.:</label>
                            <div className="col-lg-6 col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="discount_amount"
                                    value={formData.discount_amount}
                                    onChange={handleDiscountChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label">Sub Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="sub_total"
                                    value={formData.sub_total}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label">VAT Amt.:</label>
                            <div className="col-lg-6 col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="vat_amount"
                                    value={formData.vat_amount}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label">Net Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="net_total"
                                    value={formData.net_total}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>

                        {formData.id && (
                            <>
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Advance:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <div className="form-control-plaintext text-end">{formatCurrency(formData.advance_payment || 0)}</div>
                                    </div>
                                </div>
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Payment:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <div className="form-control-plaintext text-end">{formatCurrency((formData.net_total - formData.advance_payment) || 0)}</div>
                                    </div>
                                </div> 
                            </>
                        )}


                    </div>

                    {!formData.id && (
                        
                        <div className="col-12 col-md-6 order-md-1">
                            <div className="mb-2 row">
                                <label className="col-lg-6 col-md-6 col-form-label">Advance:</label>
                                <div className="col-lg-6 col-md-6">
                                    <CommonInput
                                        type="number"
                                        min={0}
                                        className="mb-0 custom-style text-end"
                                        name="advance_payment"
                                        value={formData.advance_payment}
                                        onChange={handleChange}
                                        disabled={true}
                                        errors={errors}
                                    />
                                </div>
                            </div>
                            <div className="mb-2 row">
                                <label className="col-lg-6 col-md-6 col-form-label">Balance:</label>
                                <div className="col-lg-6 col-md-6">
                                    <CommonInput
                                        type="number"
                                        min={0}
                                        className="mb-0 custom-style text-end"
                                        name="balance"
                                        value={formData.balance}
                                        onChange={handleChange}
                                        disabled={true}
                                        errors={errors}
                                    />
                                </div>
                            </div>
                            <div className="mb-2 row">
                                <label className="col-lg-6 col-md-6 col-form-label">Payment:</label>
                                <div className="col-lg-6 col-md-6">
                                    <CommonInput
                                        type="number"
                                        min={0}
                                        className="mb-0 custom-style text-end"
                                        name="payment"
                                        value={formData.payment}
                                        onChange={handleChange}
                                        disabled={true}
                                        errors={errors}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-12">
                                    <div className="table-responsive mt-4 mb-2">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Amount</th>
                                                    <th>Date</th>
                                                    <th>Reference</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData?.payments && formData.payments.length > 0 ? (
                                                    formData.payments.map((payment, index) => (
                                                        <tr key={index}>
                                                            <td>{payment.payment_type}</td>
                                                            <td>{payment.payment_amount}</td>
                                                            <td>{payment.payment_date}</td>
                                                            <td>{payment.payment_reference}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => handleDeletePayment(index)}
                                                                    disabled={isSubmitting}
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center">No payments added</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-12'>
                                    <div className='row justify-content-center'>
                                        <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                            <CommonIconRadio
                                                name="payment_type"
                                                id="payment_type1"
                                                value="cash"
                                                label="Cash"
                                                leftIcon="fa fa-money-bill"
                                                onChange={handlePaymentChange}
                                                checked={formData?.payment_type === 'cash'}
                                                disabled={isSubmitting}
                                                errors={errors}
                                                tickContainerClassName="radio_tick_container"
                                            />
                                        </div>
                                        <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                            <CommonIconRadio
                                                name="payment_type"
                                                id="payment_type2"
                                                value="bank"
                                                label="Bank"  
                                                leftIcon="fa fa-bank"
                                                onChange={handlePaymentChange}
                                                checked={formData?.payment_type === 'bank'}
                                                disabled={isSubmitting}
                                                errors={errors}
                                                tickContainerClassName="radio_tick_container"
                                            />
                                        </div>
                                        <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                            <CommonIconRadio
                                                name="payment_type"
                                                id="payment_type3"
                                                value="card"
                                                label="Card"
                                                leftIcon="fa fa-credit-card"
                                                onChange={handlePaymentChange}
                                                checked={formData?.payment_type === 'card'}
                                                disabled={isSubmitting}
                                                errors={errors}
                                                tickContainerClassName="radio_tick_container"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">Payment Amount</label>
                                        <CommonInput
                                            type="number"
                                            name="payment_amount"
                                            onChange={handlePaymentChange}
                                            disabled={isSubmitting}
                                            value={formData.payment_amount}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">Payment Date</label>
                                        <CommonDate
                                            type="date"
                                            name="payment_date"
                                            onChange={handlePaymentChange}
                                            disabled={isSubmitting}
                                            value={formData.payment_date}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="mb-3">
                                        <label className="form-label">Payment Reference</label>
                                        <CommonTextarea
                                            type="text"
                                            rows={2}
                                            name="payment_reference"
                                            onChange={handlePaymentChange}
                                            disabled={isSubmitting}
                                            value={formData.payment_reference}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-12">
                                    <button type="button" className="btn btn-warning" onClick={handleAddPayment}>Add Payment</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <DataSelectionModal
                    show={customerModal.isVisible}
                    onHide={customerModal.hide}
                    title=""
                    DataComponent={Customers}
                    selectionMode="single"
                    onDataSelected={handleCustomerSelected}
                    componentProps={{
                        // Any props specific to Suppliers component
                        excludedActions: ['create', 'actions', 'filter'],
                        externalCondition: customerExternalCondition
                    }}
                />

            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default CashMemoForm; 